import React, { useState, useEffect, useMemo, useRef } from "react";
import { Select, Input, Table, Label } from "semantic-ui-react";
import { Pie, Column } from "@ant-design/plots";
import { Training } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite } from "../../../../contexts";
import { LoadingSpinner, DownloadChartsButton } from "../../../Shared";
import "./CapacitacionesDashboard.scss";

const capController = new Training();

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const y = CURRENT_YEAR - 2 + i;
  return { key: y, value: y, text: String(y) };
});

const MONTH_KEYS = ["month_jan", "month_feb", "month_mar", "month_apr", "month_may", "month_jun", "month_jul", "month_aug", "month_sep", "month_oct", "month_nov", "month_dec"];

const CHART_COLORS = [
  "#0E376E", "#1A5FA0", "#648EBC", "#3D6B9E",
  "#4F7FB5", "#A4C4E0", "#1A1C40", "#8AB3D4",
  "#C8DDF0", "#2A4F80",
];

const STATUS_LABELS = {
  pending:        "Pendiente",
  in_progress:    "En progreso",
  completed:      "Realizado",
  not_applicable: "No aplica",
  overdue:        "Vencido",
  cancelled:      "Cancelado",
};

const STATUS_COLOR = {
  pending:        "grey",
  in_progress:    "blue",
  completed:      "green",
  not_applicable: "olive",
  overdue:        "orange",
  cancelled:      "red",
};

const INSTRUCTOR_LABELS = {
  internal_ehs:           "Responsable EHS",
  occupational_physician: "Médico laboral",
  external_trainer:       "Capacitador externo",
  vendor_manufacturer:    "Proveedor / Fabricante",
  government_agency:      "Organismo oficial",
  other:                  "Otro",
};

function KPICard({ label, value, rawValue, showProgress, progressColor = "blue", subtitle }) {
  return (
    <div className={`resumen-card${!showProgress ? " no-progress" : ""}`}>
      <div className="card-meta">
        <div className="card-title-row">
          <span className="card-title">{label}</span>
        </div>
        {subtitle && <span className="card-subtitle">{subtitle}</span>}
      </div>
      <div className="card-header">
        <span>{value}</span>
      </div>
      {showProgress ? (
        <div className="progress-container">
          <div className="progress-bar">
            <div className={`progress-fill ${progressColor}`} style={{ width: `${rawValue}%` }} />
          </div>
        </div>
      ) : (
        <div className="empty-space" />
      )}
    </div>
  );
}

function EmptyMsg() {
  return <div className="cap-dashboard__empty">Sin datos</div>;
}

function getAttendancePct(item) {
  const att = item.attendees || [];
  if (!att.length) return null;
  const present = att.filter((a) => a.result !== "absent").length;
  return Math.round((present / att.length) * 100);
}

function getResponsible(item) {
  if (item.instructorName) return item.instructorName;
  return INSTRUCTOR_LABELS[item.instructor] || item.instructor || "-";
}

export function CapacitacionesDashboard({ year: yearProp, site: siteProp }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const { site: contextSite } = useSite();
  const t = (key) => translations[key] || key;

  const site = siteProp !== undefined ? siteProp : contextSite;

  const [internalYear, setInternalYear] = useState(CURRENT_YEAR);
  const chartsRef = useRef();
  const year = yearProp !== undefined ? yearProp : internalYear;
  const showYearSelector = yearProp === undefined;

  const [items, setItems] = useState(null);

  // Filters
  const [filterSearch, setFilterSearch] = useState("");
  const [filterArea,   setFilterArea]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const params = { active: true };
        if (site) params.site = site;
        if (year) params.year = year;
        const res = await capController.getCapacitaciones(accessToken, params);
        setItems(Array.isArray(res) ? res : []);
      } catch {
        setItems([]);
      }
    })();
  }, [year, site, accessToken]);

  const kpis = useMemo(() => {
    if (!items) return { total: 0, completadas: 0, pendientes: 0, cumplimiento: 0 };
    const total        = items.length;
    const completadas  = items.filter((i) => i.status === "completed").length;
    const pendientes   = items.filter((i) => i.status === "pending" || i.status === "overdue").length;
    const cumplimiento = total > 0 ? Math.round((completadas / total) * 100) : 0;
    return { total, completadas, pendientes, cumplimiento };
  }, [items]);

  const displayItems = useMemo(() => {
    if (!items) return [];
    return items.filter((i) => {
      if (filterArea   && (i.area?._id || i.area) !== filterArea)   return false;
      if (filterStatus && i.status !== filterStatus)                 return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const matches =
          (i.title || "").toLowerCase().includes(q) ||
          (i.area?.name || "").toLowerCase().includes(q) ||
          getResponsible(i).toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [items, filterSearch, filterArea, filterStatus]);

  // Chart: by area
  const areaData = useMemo(() => {
    if (!items) return [];
    const counts = {};
    items.forEach((i) => {
      const name = i.area?.name?.trim();
      if (!name) return;
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [items]);

  // Chart: by month (scheduledDate)
  const monthData = useMemo(() => {
    if (!items) return MONTH_KEYS.map((key) => ({ mes: t(key), value: 0 }));
    const counts = Array(12).fill(0);
    items.forEach((i) => {
      const d = i.scheduledDate || i.startDate;
      if (d && new Date(d).getFullYear() === year) counts[new Date(d).getMonth()]++;
    });
    return MONTH_KEYS.map((key, idx) => ({ mes: t(key), value: counts[idx] }));
  }, [items, year]);

  // Chart: by status
  const statusData = useMemo(() => {
    if (!items) return [];
    const counts = {};
    items.forEach((i) => {
      const key = STATUS_LABELS[i.status] || i.status || "Sin estado";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [items]);

  const pieConfig = (data) => {
    const colors = data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
    const total  = data.reduce((sum, d) => sum + d.value, 0);
    return {
      data,
      angleField: "value",
      colorField: "type",
      innerRadius: 0.6,
      scale: { color: { range: colors } },
      label: false,
      legend: false,
      tooltip: {
        title: "type",
        items: [{
          field: "value",
          name: "Cantidad",
          valueFormatter: (v) => `${v} (${total > 0 ? ((v / total) * 100).toFixed(1) : "0.0"}%)`,
        }],
      },
      height: 250,
    };
  };

  const colMonthConfig = {
    data: monthData,
    xField: "mes",
    yField: "value",
    height: 250,
    style: { fill: "#648EBC" },
    tooltip: { items: [{ channel: "y", name: "Capacitaciones" }] },
  };

  const hasMonthData = monthData.some((d) => d.value > 0);

  const areaOptions = useMemo(() => {
    if (!items) return [];
    const seen = new Map();
    items.forEach((i) => {
      if (i.area?._id) seen.set(i.area._id, i.area.name);
    });
    return [
      { key: "all", value: "", text: "Todas las áreas" },
      ...[...seen.entries()].map(([id, name]) => ({ key: id, value: id, text: name })),
    ];
  }, [items]);

  const STATUS_OPTIONS = [
    { key: "all",            value: "",             text: "Todos los estados" },
    { key: "pending",        value: "pending",        text: "Pendiente" },
    { key: "in_progress",    value: "in_progress",    text: "En progreso" },
    { key: "completed",      value: "completed",      text: "Realizado" },
    { key: "overdue",        value: "overdue",        text: "Vencido" },
    { key: "not_applicable", value: "not_applicable", text: "No aplica" },
    { key: "cancelled",      value: "cancelled",      text: "Cancelado" },
  ];

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="cap-dashboard">
      {/* KPI wrapper */}
      <div className="resumen-wrapper cap-dashboard__kpi-wrapper">
        <div className="cap-dashboard__header">
          {showYearSelector && (
            <Select
              options={YEAR_OPTIONS}
              value={year}
              onChange={(_, d) => setInternalYear(d.value)}
              className="cap-dashboard__year-select"
            />
          )}
          <DownloadChartsButton targetRef={chartsRef} filename="capacitaciones" />
        </div>
        <div className="cap-dashboard__kpis">
          <KPICard label="Total capacitaciones" value={kpis.total} />
          <KPICard
            label="Cumplimiento"
            value={`${kpis.cumplimiento}%`}
            rawValue={kpis.cumplimiento}
            showProgress
            progressColor="blue"
          />
          <KPICard label="Completadas" value={kpis.completadas} />
          <KPICard label="Pendientes / Vencidas" value={kpis.pendientes} />
        </div>
      </div>

      {/* Charts */}
      <div className="cap-dashboard__charts" ref={chartsRef}>
        <div className="cap-dashboard__chart-box">
          <div className="cap-dashboard__chart-title">Por área</div>
          <div className="chartWrapper">
            {areaData.length ? <Pie {...pieConfig(areaData)} /> : <EmptyMsg />}
          </div>
          {areaData.length > 0 && (
            <div className="pillsGrid">
              {areaData.map((d, i) => (
                <span key={d.type} className="yearPill" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                  {d.type}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="cap-dashboard__chart-box">
          <div className="cap-dashboard__chart-title">Fechas programadas por mes</div>
          <div className="chartWrapper">
            {hasMonthData ? <Column {...colMonthConfig} /> : <EmptyMsg />}
          </div>
          {hasMonthData && (
            <div className="pillsRow">
              <span className="yearPill" style={{ backgroundColor: "#648EBC" }}>Capacitaciones</span>
            </div>
          )}
        </div>

        <div className="cap-dashboard__chart-box">
          <div className="cap-dashboard__chart-title">Por estado</div>
          <div className="chartWrapper">
            {statusData.length ? <Pie {...pieConfig(statusData)} /> : <EmptyMsg />}
          </div>
          {statusData.length > 0 && (
            <div className="pillsGrid">
              {statusData.map((d, i) => (
                <span key={d.type} className="yearPill" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                  {d.type}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="cap-dashboard__filters">
        <div className="cap-dashboard__search-wrapper">
          <Input
            icon="search"
            placeholder="Buscar capacitación..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            transparent
          />
        </div>
        <Select
          options={areaOptions}
          value={filterArea}
          onChange={(_, d) => setFilterArea(d.value)}
          placeholder="Área"
          className="cap-dashboard__filter-select"
          clearable
        />
        <Select
          options={STATUS_OPTIONS}
          value={filterStatus}
          onChange={(_, d) => setFilterStatus(d.value)}
          placeholder="Estado"
          className="cap-dashboard__filter-select"
          clearable
        />
      </div>

      {/* Table */}
      <div className="cap-dashboard__table-section">
        <div className="cap-dashboard__ro-table-wrapper">
          <Table className="cap-dashboard__ro-table">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Capacitación</Table.HeaderCell>
                <Table.HeaderCell>Área</Table.HeaderCell>
                <Table.HeaderCell>Responsable</Table.HeaderCell>
                <Table.HeaderCell>Estado</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Asistencia</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {displayItems.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center" style={{ color: "#999", padding: "1.5rem" }}>
                    Sin resultados
                  </Table.Cell>
                </Table.Row>
              ) : displayItems.map((item) => {
                const pct = getAttendancePct(item);
                return (
                  <Table.Row key={item._id}>
                    <Table.Cell style={{ textAlign: "left" }}>{item.title || "-"}</Table.Cell>
                    <Table.Cell>{item.area?.name || "-"}</Table.Cell>
                    <Table.Cell>{getResponsible(item)}</Table.Cell>
                    <Table.Cell>
                      <Label size="small" color={STATUS_COLOR[item.status] || "grey"}>
                        {STATUS_LABELS[item.status] || item.status || "-"}
                      </Label>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {pct !== null ? `${pct}%` : "-"}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      </div>
    </div>
  );
}
